import { Title } from '@solidjs/meta'
import { A, useParams, useSearchParams } from '@solidjs/router'
import { type Component, createMemo, createSignal, For, Show } from 'solid-js'
import { z } from 'zod'
import Alert from '~/components/Alert'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Container from '~/components/Container'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Textarea from '~/components/Textarea'
import { useForm } from '~/hooks/useForm'
import { type Path, useNavigate } from '~/router'
import { checkoutService } from '~/services/checkout.service'
import { type CartLineType, cartLines, clearBuyNow, clearCart, getBuyNow } from '~/stores/cart'
import { resolveError } from '~/utils/error'

const money = (value: number) => `$${value.toLocaleString('es-CO')}`

/**
 * Página de pago.
 *
 * Sirve para las dos formas de comprar: el carrito, y la **compra inmediata**
 * (`?directo=1`), que trae un único artículo sin tocar lo que el comprador
 * tuviera apartado.
 *
 * Lo que se manda al backend es sólo qué y cuánto. Los precios que se ven aquí
 * son los que se guardaron al añadir: **el total que se cobra lo calcula el
 * servidor** contra el catálogo, así que si algo cambió de precio, manda el suyo.
 */
const PagoPage: Component = () => {
  const params = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const empresa = () => params.empresa ?? ''
  const esDirecto = () => searchParams.directo === '1'

  const [errorApi, setErrorApi] = createSignal<string>()

  /**
   * La compra inmediata se lee una sola vez: si se releyera en cada render, un
   * cambio en sessionStorage a medio pagar movería el pedido bajo los pies.
   */
  const [compraDirecta] = createSignal<CartLineType | null>(esDirecto() ? getBuyNow(empresa()) : null)

  const lines = createMemo<CartLineType[]>(() => {
    const directa = compraDirecta()
    if (esDirecto()) return directa ? [directa] : []
    return cartLines(empresa())
  })

  const subtotal = createMemo(() => lines().reduce((sum, l) => sum + l.unitPrice * l.quantity, 0))

  /** Un carrito sólo de servicios no necesita dirección de entrega. */
  const necesitaEnvio = createMemo(() => lines().some(l => l.trackInventory))

  const checkoutSchema = z.object({
    name: z.string().trim().min(1, 'Necesitamos tu nombre'),
    email: z.string().trim().min(1, 'Necesitamos tu correo').email('Ese correo no parece válido'),
    phone: z.string().trim().min(7, 'Necesitamos un teléfono de contacto'),
    document: z.string().trim().default(''),
    address: z.string().trim().default(''),
    city: z.string().trim().default(''),
    state: z.string().trim().default(''),
    notes: z.string().trim().default(''),
    acepta: z.boolean().refine(v => v === true, 'Debes aceptar para continuar')
  })

  const form = useForm({
    schema: checkoutSchema,
    initialValues: () => ({
      name: '',
      email: '',
      phone: '',
      document: '',
      address: '',
      city: '',
      state: '',
      notes: '',
      acepta: false
    }),
    onSubmit: async values => {
      setErrorApi(undefined)
      if (lines().length === 0) return

      const { order, checkoutUrl } = await checkoutService.create(empresa(), {
        items: lines().map(l => ({
          productId: l.productId,
          variantId: l.variantId,
          quantity: l.quantity
        })),
        buyer: {
          name: values.name as string,
          email: values.email as string,
          phone: values.phone as string,
          document: (values.document as string) || undefined
        },
        shipping: necesitaEnvio()
          ? {
              address: (values.address as string) || undefined,
              city: (values.city as string) || undefined,
              state: (values.state as string) || undefined
            }
          : null,
        notes: (values.notes as string) || null,
        // El número de pedido aún no existe, así que la clave la pone el
        // navegador: dos envíos del mismo formulario son la misma compra.
        idempotencyKey: `${empresa()}-${values.email}-${subtotal()}-${lines().length}`
      })

      // El pedido ya está creado: lo que había apartado sobra. Si el pago falla,
      // se reintenta desde la página del pedido, no desde el carrito.
      if (esDirecto()) clearBuyNow()
      else clearCart(empresa())

      if (checkoutUrl) {
        window.location.href = checkoutUrl
        return
      }
      // Sin URL de pago (ya estaba pagado) se va al estado del pedido.
      navigate(`/catalogo/${empresa()}/pedido/${order.number}` as Path)
    },
    onError: err => setErrorApi(resolveError(err))
  })

  return (
    <>
      <Title>Finalizar compra</Title>

      <Container class="py-8">
        <div class="mb-6 flex items-center justify-between gap-3">
          <h1 class="text-2xl font-bold text-foreground">Finalizar compra</h1>
          <A href={esDirecto() ? `/catalogo/${empresa()}` : `/catalogo/${empresa()}/carrito`}>
            <Button variant="text" color="default" prependIcon="material-symbols-light:arrow-back">
              Volver
            </Button>
          </A>
        </div>

        <Show
          when={lines().length}
          fallback={
            <Card>
              <Card.Body>
                <div class="flex flex-col items-center justify-center gap-4 py-14 text-center">
                  <Icon icon="material-symbols-light:shopping-cart-outline" size={52} class="text-foreground-subtle" />
                  <p class="text-foreground-subtle">No hay nada que pagar. Puede que la compra ya se completara o que se recargara la página.</p>
                  <A href={`/catalogo/${empresa()}`}>
                    <Button>Ver el catálogo</Button>
                  </A>
                </div>
              </Card.Body>
            </Card>
          }
        >
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-5 items-start">
            {/* Datos del comprador */}
            <div class="lg:col-span-3">
              <Card>
                <Card.Header>
                  <h2 class="text-lg font-semibold text-foreground">Tus datos</h2>
                </Card.Header>
                <Card.Body>
                  <Show when={errorApi()}>
                    <Alert variant="danger" class="mb-4">
                      {errorApi()}
                    </Alert>
                  </Show>

                  <Form id="checkout-form" controller={form}>
                    <div class="space-y-4">
                      <Input name="name" label="Nombre completo" placeholder="Ana Pérez" />

                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input name="email" label="Correo" type="email" placeholder="ana@correo.com" helperText="Aquí te llega el comprobante de tu compra." />
                        <Input name="phone" label="Teléfono" placeholder="300 123 4567" />
                      </div>

                      <Input name="document" label="Documento (opcional)" placeholder="Cédula o NIT" />

                      {/* Un pedido sólo de servicios no se despacha a ningún sitio. */}
                      <Show when={necesitaEnvio()}>
                        <div class="pt-2 border-t border-border space-y-4">
                          <h3 class="text-sm font-semibold text-foreground">Entrega</h3>
                          <Input name="address" label="Dirección" placeholder="Calle 10 # 20-30, apto 401" />
                          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input name="city" label="Ciudad" placeholder="Medellín" />
                            <Input name="state" label="Departamento" placeholder="Antioquia" />
                          </div>
                        </div>
                      </Show>

                      <Textarea name="notes" label="Notas para el vendedor (opcional)" rows={2} placeholder="Alguna indicación sobre tu pedido" />

                      <Checkbox name="acepta" label="Acepto que se procese mi pedido y mis datos de contacto" checked={Boolean(form.values().acepta)} />
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </div>

            {/* Resumen del pedido */}
            <div class="lg:col-span-2 lg:sticky lg:top-4">
              <Card>
                <Card.Header>
                  <h2 class="text-lg font-semibold text-foreground">Tu pedido</h2>
                </Card.Header>
                <Card.Body>
                  <div class="space-y-3">
                    <For each={lines()}>
                      {line => (
                        <div class="flex gap-3">
                          <Show when={line.image}>
                            <img src={line.image as string} alt={line.name} class="h-12 w-12 shrink-0 rounded-md object-cover" />
                          </Show>
                          <div class="min-w-0 flex-1">
                            <p class="text-sm font-medium text-foreground leading-tight">{line.name}</p>
                            <Show when={line.variantLabel}>
                              <p class="text-xs text-foreground-subtle">{line.variantLabel}</p>
                            </Show>
                            <p class="text-xs text-foreground-subtle">
                              {line.quantity} × {money(line.unitPrice)}
                            </p>
                          </div>
                          <span class="text-sm font-semibold text-foreground shrink-0">{money(line.unitPrice * line.quantity)}</span>
                        </div>
                      )}
                    </For>
                  </div>

                  <dl class="mt-4 space-y-2 border-t border-border pt-3 text-sm">
                    <div class="flex justify-between">
                      <dt class="text-foreground-subtle">Subtotal</dt>
                      <dd>{money(subtotal())}</dd>
                    </div>
                    <div class="flex justify-between border-t border-border pt-2 text-base font-bold">
                      <dt>Total</dt>
                      <dd>{money(subtotal())}</dd>
                    </div>
                  </dl>

                  {/* El form vive en la otra columna: sin `form` no enviaría nada. */}
                  <Button fullWidth size="lg" class="mt-4" type="submit" form="checkout-form" disabled={form.submitting()} prependIcon="material-symbols-light:lock-outline">
                    {form.submitting() ? 'Creando tu pedido…' : 'Pagar ahora'}
                  </Button>

                  <p class="mt-3 flex items-center justify-center gap-1 text-center text-xs text-foreground-subtle">
                    <Icon icon="material-symbols-light:shield-outline" size={14} />
                    Te llevaremos a la pasarela para pagar de forma segura.
                  </p>
                  <p class="mt-1 text-center text-xs text-foreground-subtle">El total definitivo lo confirma el vendedor al crear el pedido.</p>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Show>
      </Container>
    </>
  )
}

export default PagoPage
